<script setup lang="ts">
const { t: $t } = useI18n()

import type { ApiInputLogisticsUpdateStatus, ApiInputOfferPositionUpdateStatus, ApiLogisticsTaskGetById } from '~/types'
import { updateLogisticsTaskStatusSchema } from '~/server/schemas'
import { LogisticsTaskStatus } from '~/prisma/enums'

const { openDriverLoadingChecklistPopup: { data: popupData, close: closeChecklistPopup } } = useGlobalOpeners()

const { logisticsTask: queryLogisticsTask } = useQuery()

const logisticsTaskId = computed(() => popupData.value?.id)
const { data: logisticsTask } = queryLogisticsTask.byIdOrThrow(logisticsTaskId)

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const disableAutomaticEmail = ref(false)
const update = useMutation({
  mutationFn: async ({ positions, task }: { positions: ApiInputOfferPositionUpdateStatus, task: ApiInputLogisticsUpdateStatus }) => {
    // TODO: Update this in one api call using transaction
    // This can result in `half-updated` state if one of the calls fails or the user closes the popup too early
    // Issue: https://github.com/sidestream-tech/hanselmann-os/issues/5038
    await $trpc.logisticsTask.updateStatus.mutate(task)
    return $trpc.offerPosition.updateStatus.mutate(positions)
  },
  onError: makeTrpcErrorToast(notification, { description: $t('logisticsTask.loadingChecklist.update.error') }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['logisticsTask'] }),
      queryClient.invalidateQueries({ queryKey: ['offer'] }),
    ])

    disableAutomaticEmail.value = false
    closeChecklistPopup()

    notification.success({ title: $t('logisticsTask.loadingChecklist.update.success'), duration: 4500 })
  },
})

const { hasAnyAssignedRole } = useAuthorization()
const isDepartmentLead = hasAnyAssignedRole(['departmentLead'])

function updateIsLoaded(task: ApiLogisticsTaskGetById) {
  const positions = task.positionsToShip.map(p => ({ id: p.id, isLoaded: p.isLoaded }))
  const areNoPositionsLoaded = task.positionsToShip.every(p => !p.isLoaded)
  const taskPayload = {
    id: task.id,
    type: task.type,
    status: areNoPositionsLoaded ? LogisticsTaskStatus.created : LogisticsTaskStatus.loaded,
    disableAutomaticEmail: disableAutomaticEmail.value,
    ...((task.type === 'outbound' && task.doesTransportationCompanyDoTask) && {
      transportationCompanyDriverName: task.transportationCompanyDriverName,
      transportationCompanyDriverSignature: task.transportationCompanyDriverSignature,
      transportationCompanyLicensePlateNumber: task.transportationCompanyLicensePlateNumber,
    }),
  }

  return update.mutate({
    positions,
    task: updateLogisticsTaskStatusSchema.parse(taskPayload),
  })
}
</script>

<template>
  <ThePopup v-if="popupData && logisticsTask" :show="Boolean(logisticsTask)" :title="$t('logisticsTask.loadingChecklist.title')" @close="closeChecklistPopup">
    <DriverLoadingChecklistForm
      :logistics-task="logisticsTask"
      :disabled="update.isPending.value"
      @save="updateIsLoaded"
    />
    <FormKit
      v-if="isDepartmentLead"
      v-model="disableAutomaticEmail"
      type="checkbox"
      :label="$t('offer.disableAutomaticLifecycleEmail')"
    />
  </ThePopup>
</template>
